import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import UserTable from '../../components/ViewAllUsers/UserTable';
import UserDetailSidebar from '../../components/ViewAllUsers/UserDetailSidebar';
import UserReassignmentModal from '../../components/ViewAllUsers/UserReassignmentModal';
import { User } from "../../components/types/user";
import { FloatingActions } from '../../components/newTpoForm';
import { getTPOUsers } from "../../services/apiclient";

const TpoView = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: '',
    blockStatus: ''
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [users, setUsers] = useState<User[]>([]); // Assume users are provided externally
  const [totalPages, setTotalPages] = useState(1);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [userToReassign, setUserToReassign] = useState<User | null>(null);
  const [userAdded, setUserAdded] = useState(false);
  const [userEdited, setUserEdited] = useState(false);
  const [blockLoading, setBlockLoading] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const activeFilters = Object.entries(filters).filter(([_, value]) => value !== '');

  // Handler for sorting
  const onSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Handler for page change
  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  const removeFilter = (key: string) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setFilters({ role: '', blockStatus: '' });
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Block/Unblock user handler (simulated without API)
  const handleBlockToggle = (user: User) => {
    if (!user) return;

    setBlockLoading(user.id);
    const newBlockedStatus = !user.isBlocked;

    try {
      // Simulate block/unblock update
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === user.id ? { ...u, isBlocked: newBlockedStatus } : u
        )
      );

      if (selectedUser && selectedUser.id === user.id) {
        setSelectedUser({ ...selectedUser, isBlocked: newBlockedStatus });
      }
    } catch (error) {
      console.error('Failed to update user status:', error);
    } finally {
      setBlockLoading(null);
    }
  };

  // Bulk block/unblock users (simulated without API)
  const handleBulkBlockToggle = (action: 'block' | 'unblock') => {
    if (selectedUsers.length === 0) {
      return;
    }

    setBulkActionLoading(true);
    const isBlocking = action === 'block';

    try {
      // Simulate bulk update
      setUsers(prevUsers =>
        prevUsers.map(u =>
          selectedUsers.includes(u.id)
            ? { ...u, status: isBlocking ? 'blocked' : 'active' }
            : u
        )
      );

      if (selectedUser && selectedUsers.includes(selectedUser.id)) {
        setSelectedUser({ ...selectedUser, isBlocked: isBlocking });
      }

      setSelectedUsers([]);
    } catch (error) {
      console.error(`Failed to ${action} users:`, error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Select/deselect all users on current page
  const handleSelectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  // Toggle user selection
  const handleUserSelect = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Handle Add TPO button click
  const handleAddTPO = () => {
    // Placeholder for Add TPO functionality
    console.log('Add TPO button clicked');
    setUserAdded(true); // Trigger user added state
  };

  const handleReassignUser = (user: User) => {
    setUserToReassign(user);
    setIsReassignModalOpen(true);
  };

  const handleReassignSuccess = () => {
    // Simulate reassignment success
    setIsReassignModalOpen(false);
    setUserToReassign(null);
  };

  // Handle user updates from sidebar
const handleUserUpdate = (updatedUser: User) => {
  setUsers((prevUsers) =>
    prevUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u))
  );
  setSelectedUser(updatedUser); // <-- This ensures sidebar updates instantly
};

  const renderPagination = () => {
    const pageNumbers = [];
    const maxPagesToShow = 6;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1 || isLoading}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {pageNumbers.map(page => (
          <Button
            key={page}
            variant={currentPage === page ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCurrentPage(page)}
            disabled={isLoading}
            className={currentPage === page ? 'bg-[#EC6E51] text-primary-foreground' : ''}
          >
            {page}
          </Button>
        ))}

        {endPage < totalPages && (
          <>
            <span className="text-sm text-muted-foreground">...</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={isLoading}
            >
              {totalPages}
            </Button>
          </>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages || isLoading}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  useEffect(() => {
    setIsLoading(true);
    getTPOUsers({
      page: currentPage,
      limit: itemsPerPage,
      search: searchTerm,
      role: filters.role,
      blockStatus: filters.blockStatus,
    })
      .then((data) => {
        setUsers(data?.data?.users || []);
        setTotalPages(data?.totalPages || 1);
      })
      .catch((err) => {
        setUsers([]);
        setTotalPages(1);
      })
      .finally(() => setIsLoading(false));
  }, [currentPage, itemsPerPage, searchTerm, filters.role, filters.blockStatus]);

  // Example: get currentUserRole from context, props, or however your app provides it
const currentUserRole = "FBDE"; // Replace with your actual logic

  return (
    <div className="max-h-screen bg-background">
      <div className="sticky top-0 z-40 bg-background border-b border-border">
        <div className="px-6 py-4">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search by name, email or phone"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10"
                  style={{ maxWidth: "550px" }}
                />
              </div>
              {currentUserRole !== "FBDE" && (
                <Select
                  value={filters.role || 'all'}
                  onValueChange={(value) => {
                    setFilters(prev => ({ ...prev, role: value === 'all' ? '' : value }));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                    <SelectItem value="HM">HM</SelectItem>
                    <SelectItem value="SHM">SHM</SelectItem>
                    <SelectItem value="FBDE">FBDE</SelectItem>
                    <SelectItem value="ADMIN">ADMIN</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Select
                value={filters.blockStatus || 'all'}
                onValueChange={(value) => {
                  setFilters(prev => ({ ...prev, blockStatus: value === 'all' ? '' : value }));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
              <div>
                <FloatingActions userAdded={userAdded} setUserAdded={setUserAdded} />
              </div>
            </div>

            {(activeFilters.length > 0 || searchTerm) && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground">Active filters:</span>
                {searchTerm && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Search: {searchTerm}
                    <X
                      className="w-3 h-3 cursor-pointer"
                      onClick={() => {
                        setSearchTerm('');
                        setCurrentPage(1);
                      }}
                    />
                  </Badge>
                )}
                {activeFilters.map(([key, value]) => (
                  <Badge key={key} variant="secondary" className="flex items-center gap-1">
                    {key}: {value}
                    <X
                      className="w-3 h-3 cursor-pointer"
                      onClick={() => removeFilter(key)}
                    />
                  </Badge>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-xs"
                >
                  Clear all
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex overflow-y-scroll" style={{ height: '70vh' }}>
        <div className={`flex-1 transition-all duration-300 ${selectedUser ? 'mr-96' : ''}`}>
          <UserTable
            users={users}
            setUsers={setUsers}
            isloading={isLoading}
            onUserSelect={setSelectedUser}
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={onSort}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onUserReassign={handleReassignUser}
            onBlockToggle={handleBlockToggle}
            blockLoading={blockLoading}
            onSourceChange={() => {}}
            isListingTPO={true} // Add a no-op or your actual handler here
          />
        </div>
        {selectedUser && (
          <UserDetailSidebar
  user={selectedUser}
  onClose={() => setSelectedUser(null)}
  // onUserUpdate={handleUserUpdate}
  setUserEdited={setUserEdited}
/>
        )}
      </div>

      <div className="flex items-center gap-2 justify-end pr-4">
        {renderPagination()}
      </div>

      <UserReassignmentModal
        isOpen={isReassignModalOpen}
        onClose={() => setIsReassignModalOpen(false)}
        user={userToReassign}
        onSuccess={handleReassignSuccess}
      />
    </div>
  );
};

export default TpoView;